// src/components/ImpersonationBanner.js
import React, { useState } from "react";
import { signInWithCustomToken } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../lib/firebase";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft } from "lucide-react";

const returnFromImpersonationFn = httpsCallable(functions, "returnFromImpersonation");

// The "return to admin" trip can't just replay a token minted back when
// impersonation started — custom tokens expire after an hour, and a
// support session can easily run longer than that. Instead, the secret
// stashed here (see impersonateBusiness in admin-actions.js) is exchanged
// for a freshly minted admin token on the way out, via
// returnFromImpersonation — by the time this banner's "Exit" is clicked,
// the current session is the impersonated business, not the admin, so
// there's no way to re-call an admin-gated function directly.
export default function ImpersonationBanner() {
  const { businessProfile } = useAuth();
  const [exiting, setExiting] = useState(false);
  const returnSecret = typeof window !== "undefined" ? sessionStorage.getItem("adminReturnSecret") : null;
  const returnBusinessId = typeof window !== "undefined" ? sessionStorage.getItem("adminReturnBusinessId") : null;

  if (!returnSecret) return null;

  async function exit() {
    setExiting(true);
    try {
      const res = await returnFromImpersonationFn({ businessId: returnBusinessId, returnSecret });
      sessionStorage.removeItem("adminReturnSecret");
      sessionStorage.removeItem("adminReturnBusinessId");
      await signInWithCustomToken(auth, res.data.returnToken);
      // Hard reload rather than client-side navigation — clean reset of
      // AuthContext and any business-specific state cached while
      // impersonating, rather than trying to reconcile it live.
      window.location.href = "/admin/businesses";
    } catch (err) {
      alert("Couldn't return to admin: " + err.message);
      setExiting(false);
    }
  }

  return (
    <div style={{
      background: "#7c3aed", color: "#fff", padding: "10px 20px",
      display: "flex", alignItems: "center", justifyContent: "center", gap: 12, flexWrap: "wrap",
      fontSize: 13, fontWeight: 600, position: "sticky", top: 0, zIndex: 200,
    }}>
      <span>👁 Viewing as <strong>{businessProfile?.businessName || "this business"}</strong> (impersonated)</span>
      <button
        onClick={exit}
        disabled={exiting}
        style={{
          display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,.15)",
          border: "1px solid rgba(255,255,255,.3)", borderRadius: 99, padding: "5px 14px",
          color: "#fff", fontSize: 12, fontWeight: 700, cursor: exiting ? "not-allowed" : "pointer",
        }}
      >
        <ArrowLeft size={13} /> {exiting ? "Returning…" : "Return to admin"}
      </button>
    </div>
  );
}
